import * as React from 'react';
import useForceUpdate from "./useForceUpdate";
import { getCommonError } from "./mergeCommonErrors";
import { getHttpError } from "./extractHttpError";
import { isPromise } from "./isPromise";
import { CancellablePromise, ThenArg } from "../interfaces/api";
import CancelablePromise from "cancelable-promise";
import { useRefValue } from "./useRefValue";

type DataContainerLoadResult<T> = T | Promise<T> | CancellablePromise<T>

export type DataContainerProps = {
  silentReload?: boolean,
  resetData?: boolean,
}

type DataContainerReload = (props?: { silent?: boolean }) => any;

type DataContainerSet<D> = (data: D) => any;

export type DataContainer<D> = {
  loading: boolean,
  error: string,
  data: D,
  reload: DataContainerReload,
  set: DataContainerSet<D>,
  promise: Promise<D> | CancellablePromise<D>,
}

enum DataContainerActionType {
  start,
  done,
  error
}

type DataContainerActionStart = { type: DataContainerActionType.start, reset: boolean, silent: boolean, promise: DataContainer<any>['promise'] };
type DataContainerActionDone = { type: DataContainerActionType.done, data: any };
type DataContainerActionError = { type: DataContainerActionType.error, error: string };
type DataContainerAction =
  | DataContainerActionStart
  | DataContainerActionDone
  | DataContainerActionError

const dataContainerActions: Record<DataContainerActionType, (state: DataContainer<any>, action: DataContainerAction) => DataContainer<any>> = {
  [DataContainerActionType.start]: (state, action: DataContainerActionStart) => state.loading || action.silent
    ? { ...state, promise: action.promise || state.promise }
    : ({
      loading: true,
      error:   null,
      data:    action.reset ? null : state.data,
      reload:  state.reload,
      set:     state.set,
      promise: action.promise || state.promise,
    }),

  [DataContainerActionType.done]:  (state, action: DataContainerActionDone) => ({
    loading: false,
    error:   null,
    data:    action.data,
    reload:  state.reload,
    set:     state.set,
    promise: state.promise,
  }),
  [DataContainerActionType.error]: (state, action: DataContainerActionError) => ({
    loading: false,
    error:   action.error,
    data:    null,
    reload:  state.reload,
    set:     state.set,
    promise: state.promise,
  }),
}

const dataContainerReducer = (state: DataContainer<any>, action: DataContainerAction) => dataContainerActions[action.type](
  state,
  action,
);

const getDataContainerInitialState = (reload: DataContainerReload, set: DataContainerSet<any>, promise: Promise<any> | CancellablePromise<any>): DataContainer<any> => ({
  loading: true,
  error:   null,
  data:    null,
  reload,
  set,
  promise,
});

const createInitialPromise = (): { used: boolean, res: (value: any) => void, rej: (error: any) => void, promise: CancellablePromise<any> } => {
  let res;
  let rej;
  const promise = new CancelablePromise((_res, _rej) => {
    res = _res;
    rej = _rej
  });
  promise.catch(() => {});
  return { used: false, res, rej, promise: promise as unknown as CancellablePromise<any> };
}

export const useDataContainer = <F>(dataLoader: () => DataContainerLoadResult<F>, config?: DataContainerProps): DataContainer<ThenArg<F>> => {
  const [forceValue, forceUpdate] = useForceUpdate();

  const reload: DataContainerReload = React.useCallback((params) => {
    silentRef.current = params?.silent || false;
    forceUpdate();
  }, []);

  const set: DataContainerSet<F> = React.useCallback((data) => dispatch({
    type: DataContainerActionType.done,
    data,
  }), []);


  const initialPromiseRef = React.useRef<ReturnType<typeof createInitialPromise>>();
  if (!initialPromiseRef.current) initialPromiseRef.current = createInitialPromise();

  const initialStateRef = React.useRef<ReturnType<typeof getDataContainerInitialState>>();
  if (!initialStateRef.current) initialStateRef.current = getDataContainerInitialState(
    reload,
    set,
    initialPromiseRef.current.promise,
  );

  const silentRef = React.useRef<boolean>();
  const requestRef = React.useRef<CancellablePromise<any>>();
  const [state, dispatch] = React.useReducer(dataContainerReducer, initialStateRef.current);
  const stateRef = useRefValue(state);

  React.useEffect(() => {
    let request = dataLoader();
    if (!isPromise(request)) request = CancelablePromise.resolve(request) as unknown as CancellablePromise<any>;

    const initialPromise = initialPromiseRef.current;
    const isInitialPromiseActive = !initialPromise.used && initialPromise.promise === stateRef.current.promise;
    const promise = isInitialPromiseActive
      ? null
      : request;

    dispatch({
      type:   DataContainerActionType.start,
      silent: (config ? config.silentReload : false) || silentRef.current,
      reset:  (config && typeof config.resetData === 'boolean') ? config.resetData : true,
      promise,
    });
    silentRef.current = false;

    if (isPromise(request)) {
      requestRef.current = request;
      request.then(
        (response) => {
          dispatch({ type: DataContainerActionType.done, data: response })
          if (isInitialPromiseActive) initialPromise.res(response);
        },
        (error) => {
          error = getCommonError(getHttpError(error))
          dispatch({ type: DataContainerActionType.error, error });
          if (isInitialPromiseActive) initialPromise.rej(error);
        },
      ).finally(() => {
        initialPromise.used = true;
        requestRef.current = null;
      });
    }

    return () => {
      const currentRequest = requestRef.current;
      if (currentRequest) {
        currentRequest.cancel && currentRequest.cancel();
        if (isInitialPromiseActive) initialPromise.promise.cancel();
      }
    }
  }, [dataLoader, forceValue]);

  return state;
};
